/**
 * A record's appearance: its icon and colour.
 *
 * A record row (a link's target, a backlink, a parent) carries `schema_id` and
 * `type` but not the icon or colour — those live on the schema and the type, which
 * the catalog already holds. A field's `value` returns record targets through
 * `withAppearance`, so a chip reads `icon` and `color` off the value it is
 * given and looks nothing up.
 *
 * The icon and colour are the record's own schema's, else its type's default
 * schema's, else a fallback. They are read from the registries at call time and
 * not remembered, so a schema edited mid-session shows on the next read; each
 * call returns a new object, which callers must not rely on for identity.
 */
import { getSchema } from "../registries/schemas.js"
import { getType } from "../registries/types.js"

export const FALLBACK_RECORD_ICON = "circle-question"
export const FALLBACK_RECORD_COLOR = "#3273dc"

interface RecordLike {
  type?: unknown
  schema_id?: unknown
  icon?: unknown
  color?: unknown
}

/**
 * `row` with its `icon` and `color` filled in, when it is a record row (an object
 * with a `type`). Anything else passes through untouched: a raw id, a null, the
 * `{ id }` placeholder for a target that did not load. An `icon` or `color` the
 * row already has is kept.
 */
export function withAppearance<T>(row: T): T {
  if (row === null || typeof row !== "object" || Array.isArray(row)) return row
  const record = row as RecordLike
  if (typeof record.type !== "string") return row
  const schema = typeof record.schema_id === "number" ? getSchema(record.schema_id) : null
  const typeSchema = getType(record.type)?.schema
  return {
    ...row,
    icon: record.icon ?? (schema?.icon || typeSchema?.icon || FALLBACK_RECORD_ICON),
    color: record.color ?? (schema?.color || typeSchema?.color || FALLBACK_RECORD_COLOR),
  }
}
